import { checkPublishStatus } from '@/components/dom/upload/PublishBtn/PublishBtn';
import { bundleProject } from '@/components/dom/upload/utils';
import { filestore } from '@/filestore';
import { DEFAULT_LAUNCH_SCREEN } from '@/settings';
import { zwClient } from '@/sync/zapworks';
import { ToastsData } from '@/utils/toasts-data';
import { IImageTrackingOrientation, ITrackingTypes, IComponentType, IButtonSubCategory, IButtonCategory, IUnitTypes, IFontTypes } from '@r3f-component-data-structure';
import { clearHlsCacheById } from '@r3f/hooks';
import { return360VideoIdBySceneId } from '@r3f/utils/content360';
import { isAbstractComponent, isCurveComponent } from '@r3f/utils/general';
import { useThree } from '@react-three/fiber';
import * as Sentry from '@sentry/react';
import { image_target_type_t } from '@zappar/zappar-cv';
import { IFileType } from '@zappar/zw-api-client';
import { useEffect, useRef, useMemo, useState, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IMediaLibraryOnSubmitCategory, IModal } from '../../typings';
import { IMenuItemType } from '../components/dom/menus/MenuList/MenuList';
import { store } from '../store';
import { IContentDocActionTypes, onAddToast, onOpenModal, onSetAppClipsConverted, onSetProjectSize, onSetProjectSizeLimit, onSetShowAppClipsModal, onSetVideoProgress, onSetWhatsNewTrigger } from '../store/actions';
import { onSetComponentAction_Cn_Doc, onSetImageTracking_Cn_Doc, onSetMultipleComponentProps_Cn_Doc, onSetVideoRenditionProgressData_Cn_Doc } from '../store/actions/automerge/content-doc-actions';
import { getAllActionTargetIdsFromEntityForTrigger } from './component-action-utils';
import { createCanvasR3fComponents, getImageTrackerIdByStartSceneId, getSceneIdsByTrackerId, getScreenRelativeScaleNormalizationFactor, getSpatialSrComponentsForScene } from './general';
import { getAppClipCompatibleTriggers } from './triggers';
export const useAddTargetImageToScene = () => {
    const dispatch = useDispatch();
    const componentsById = useSelector((state) => state.contentReducer.contentDoc?.componentsById);
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const imageTrackingDict = useSelector((state) => state.contentReducer.contentDoc?.tracking?.[ITrackingTypes.image]);
    const usedTrackingImageIds = useGetSequentialTrackingImageIds();
    return (sceneId, trackingImageId) => {
        const scene = componentsById[sceneId];
        if (!scene)
            return;
        const sceneChildren = componentsById[sceneId].children;
        const wrappedEntityIds = Object.entries(componentsById).reduce((acc, [id, component]) => {
            if (sceneChildren.includes(id) && isCurveComponent(component) && component.isSnappedToTarget) {
                acc.push(id);
            }
            return acc;
        }, []);
        const previousTrackingData = imageTrackingDict?.[scene.trackingImageId];
        const previousImageType = previousTrackingData?.targetType || null;
        const newTrackingData = imageTrackingDict?.[trackingImageId];
        dispatch(onSetMultipleComponentProps_Cn_Doc([{
                id: sceneId,
                trackingImageId
            }]));
        const trackingImageData = {
            id: trackingImageId,
            lastUsed: new Date().toISOString()
        };
        // Check if the scene for which the image tracker should be replaced is a start scene for an image tracker. 
        const trackerIdWithSceneAsStartScene = getImageTrackerIdByStartSceneId(imageTrackingDict, sceneId);
        // If NO, no need for further action. The current image tracker has another scene as start scene that should remain
        // If YES, further action required. 
        if (trackerIdWithSceneAsStartScene !== null) {
            const sceneIdsWithTrackerId = getSceneIdsByTrackerId(componentsById, rootId, trackerIdWithSceneAsStartScene);
            // Check if current image tracker for scene is used more than once in project. 
            // If YES, assign current image tracker a new start scene (sequentially next scene using that image tracker)
            if (sceneIdsWithTrackerId.length > 1) {
                const indexOfSceneToDelete = sceneIdsWithTrackerId.indexOf(sceneId);
                // If scene to to replace as start scene is last scene to use tracker id, start from beginning
                const nextSceneIdWithTrackerId = sceneIdsWithTrackerId[indexOfSceneToDelete + 1] ?? sceneIdsWithTrackerId[0];
                if (nextSceneIdWithTrackerId) {
                    dispatch({
                        type: IContentDocActionTypes.SET_IMAGE_TRACKING_CN_DOC,
                        payload: { id: trackerIdWithSceneAsStartScene, startSceneId: nextSceneIdWithTrackerId },
                    });
                }
            }
        }
        // If new image tracker is not used, set scene as start scene as only used once in project
        if (!usedTrackingImageIds.includes(trackingImageId))
            trackingImageData.startSceneId = sceneId;
        dispatch(onSetImageTracking_Cn_Doc(trackingImageData));
        // If previously we had a curved image and now its flat, flatten any wrapped entities
        if (previousImageType && previousImageType !== newTrackingData?.targetType && newTrackingData?.targetType === image_target_type_t.IMAGE_TRACKER_TYPE_PLANAR && !!wrappedEntityIds.length) {
            const flattenedData = wrappedEntityIds.map(id => {
                return {
                    id,
                    curvature: 0,
                    isSnappedToTarget: false
                };
            });
            dispatch(onSetMultipleComponentProps_Cn_Doc(flattenedData));
        }
    };
};
export const useOnUnmountActionPopout = (store, dispatch, editingAction, editingActionType, data) => {
    useEffect(() => {
        return () => {
            // Do not update content doc when the type in the content doc doesn't match. This is when the user has changed the action category in the drop down menu.
            if (typeof editingAction?.actionIndex === 'undefined' || !editingAction.entityId || !editingAction.triggerType)
                return;
            const component = store.getState().contentReducer.contentDoc.componentsById[editingAction?.entityId];
            if (!component)
                return;
            if (isAbstractComponent(component) && component.type !== IComponentType.Scene)
                return;
            const currentActionType = component?.actions?.[editingAction.triggerType]?.[editingAction.actionIndex]?.type;
            if (currentActionType !== editingActionType)
                return;
            // Update content doc with updated state
            dispatch(onSetComponentAction_Cn_Doc({
                entityId: editingAction.entityId,
                index: editingAction.actionIndex,
                triggerType: editingAction.triggerType,
                // eslint-disable-next-line react-hooks/exhaustive-deps
                actionData: { ...data.current },
            }));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
export const useGetActionByEntityIdAndIndex = (entityId, trigger, actionIndex) => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!entityId || !trigger || actionIndex === undefined)
        return;
    const entity = componentsById[entityId];
    return entity?.actions?.[trigger]?.[actionIndex];
};
export const useGetOpenModal = () => {
    const openModalId = useSelector((state) => {
        const modalById = state.userReducer.modalById;
        const modalId = Object.entries(modalById).find(([_key, val]) => {
            return val === true;
        });
        return modalId?.[0] ?? null;
    });
    return openModalId;
};
export const useTimeOutOnMount = (cb, ms) => {
    useEffect(() => {
        const timout = setTimeout(() => cb(), ms);
        return () => clearTimeout(timout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
export const useComponentWillMount = (fn) => {
    const willMount = useRef(true);
    if (willMount.current) {
        fn();
    }
    willMount.current = false;
};
export const useGetScreenRelativeDomCanvasAspectRatio = () => {
    const { gl } = useThree();
    return gl.domElement.width / gl.domElement.height;
};
export const useIsWorldTrackingScene = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    return componentsById[activeSceneId]?.trackingType === ITrackingTypes.world;
};
export const useGetActiveSceneTrackingType = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    return componentsById[activeSceneId]?.trackingType || ITrackingTypes.world;
};
export const useIsActiveSceneFirstScene = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const firstSceneId = useSelector((state) => state.contentReducer.contentDoc.componentsById[rootId].children[0]);
    return activeSceneId == firstSceneId;
};
export const useActiveSceneTrackingImageId = () => {
    const activeScene = useGetCurrentScene();
    if (!activeScene)
        return undefined;
    return activeScene.trackingType === ITrackingTypes.image ? activeScene.trackingImageId : undefined;
};
export const useGetAllSceneIdsForActiveTrackingImageId = () => {
    const activeSceneTrackingImageId = useActiveSceneTrackingImageId();
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const sceneIds = useSelector((state) => state.contentReducer.contentDoc.componentsById[rootId].children);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    return sceneIds.filter(sceneId => {
        const scene = componentsById[sceneId];
        return scene.trackingType === ITrackingTypes.image && scene.trackingImageId === activeSceneTrackingImageId;
    });
};
export const useGetSequentialTrackingImageIds = () => {
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const sceneIds = useSelector((state) => state.contentReducer.contentDoc.componentsById[rootId].children);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const sequentialTrackingImageIds = [];
    for (const sceneId of sceneIds) {
        const scene = componentsById[sceneId];
        if (!scene || (scene.type !== IComponentType.Scene && scene.trackingType !== ITrackingTypes.image))
            continue;
        if (!sequentialTrackingImageIds.includes(scene.trackingImageId) && !!scene.trackingImageId)
            sequentialTrackingImageIds.push(scene.trackingImageId);
    }
    return sequentialTrackingImageIds;
};
export const useActiveSceneImageTrackingData = () => {
    const activeSceneTrackingImageId = useActiveSceneTrackingImageId() ?? '';
    return useSelector((state) => state.contentReducer.contentDoc?.tracking?.[ITrackingTypes.image]?.[activeSceneTrackingImageId]);
};
export const useActiveSceneCurvedImageTrackingRadius = () => {
    return useActiveSceneImageTrackingData()?.topRadius || 0;
};
export const useActiveSceneTargetHeightInMm = () => {
    return useActiveSceneImageTrackingData()?.targetAreaData?.heightInMm || 297;
};
export const useActiveSceneTargetDisplayUnits = () => {
    return useActiveSceneImageTrackingData()?.units || IUnitTypes.coords;
};
export const useIsActiveSceneFlatImageTracked = () => {
    return useActiveSceneImageTrackingData()?.orientation === IImageTrackingOrientation.flat;
};
export const useIsImageTrackingMissing = () => {
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const sceneIds = useSelector((state) => state.contentReducer.contentDoc.componentsById[rootId].children);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const imageTrackingData = useSelector((state) => state.contentReducer.contentDoc.tracking?.[ITrackingTypes.image]);
    const imageTrackedScenesWithoutTrackingData = sceneIds.filter(sceneId => {
        const scene = componentsById[sceneId];
        return scene.trackingType === ITrackingTypes.image && !imageTrackingData?.[scene.trackingImageId];
    });
    return imageTrackedScenesWithoutTrackingData.length > 0;
};
export const useIsTargetImageStartScene = () => {
    const activeSceneId = useGetCurrentSceneId();
    const trackingImage = useActiveSceneImageTrackingData();
    return trackingImage?.startSceneId === activeSceneId;
};
export const useTargetImageNumber = () => {
    const targetImageDict = useSelector((state) => state.contentReducer.contentDoc?.tracking?.[ITrackingTypes.image]);
    return Object.keys(targetImageDict ?? {}).length;
};
export const useIsFlatOrientation = () => {
    const activeSceneImageTracking = useActiveSceneImageTrackingData();
    return activeSceneImageTracking?.orientation === IImageTrackingOrientation.flat;
};
export const useIsReplaceEntityMode = () => {
    return useSelector((state) => state.userReducer.mediaLibraryDisplay.onSubmitCategory == IMediaLibraryOnSubmitCategory.ReplaceEntity || false);
};
export const useIsTargetImageAllowanceExceeded = () => {
    const targetImageDict = useSelector((state) => state.contentReducer.contentDoc?.tracking?.image || {});
    const projectInfo = useSelector((state) => state.userReducer.project);
    const numberTrackingImages = Object.keys(targetImageDict).length;
    return numberTrackingImages > (projectInfo.maxTargetImages ?? 0);
};
export const useIsTargetImageAllowanceReached = () => {
    const targetImageDict = useSelector((state) => state.contentReducer.contentDoc?.tracking?.image || {});
    const projectInfo = useSelector((state) => state.userReducer.project);
    const numberTrackingImages = Object.keys(targetImageDict).length;
    return numberTrackingImages === (projectInfo.maxTargetImages ?? 0);
};
export const useIsCurvedImageTrackedScene = () => {
    const isImageTrackedScene = useGetActiveSceneTrackingType() === ITrackingTypes.image;
    const targetType = useGetCurrentSceneTrackingData()?.targetType;
    const isCurved = targetType !== image_target_type_t.IMAGE_TRACKER_TYPE_PLANAR && typeof targetType !== 'undefined';
    return isImageTrackedScene && isCurved;
};
export const useActiveSceneHas3dContent = () => {
    return useSelector((state) => {
        const activeSceneId = state.userReducer.activeSceneId;
        if (!activeSceneId)
            return false;
        const { componentsById } = state.contentReducer.contentDoc;
        const { children = [] } = componentsById[activeSceneId] || {};
        return children.some((id) => {
            const component = componentsById[id];
            if (isAbstractComponent(component)) {
                return false;
            }
            return (component.scale[2] !== 0 ||
                component.position[2] !== 0 ||
                component.rotation[0] !== 0 ||
                component.rotation[1] !== 0 ||
                component.type === IComponentType.Model3d ||
                component.type === IComponentType.Emitter ||
                !!component.isSnappedToTarget ||
                !!component.curvature);
        });
    });
};
export const useGetSiblings = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const trackingType = useGetActiveSceneTrackingType();
    const screenComponentIds = useGetActiveScreenComponentIds();
    const faceTrackedComponentIds = useGetFaceTrackedComponentIds();
    if (isScreenRelativeMode)
        return screenComponentIds;
    if (trackingType === ITrackingTypes.face)
        return faceTrackedComponentIds;
    return componentsById[activeSceneId].children.filter((id) => componentsById[id] && componentsById[id].type !== IComponentType.ScreenContent);
};
export const useGetEntityTransientColors = (id) => {
    const transientBorderRgba = useSelector((state) => state.userReducer.borderRgbaById?.[id] || null);
    const transientFontRgba = useSelector((state) => state.userReducer.fontRgbaById?.[id] || null);
    const transientFillRgba = useSelector((state) => state.userReducer.fillRgbaById?.[id] || null);
    return {
        transientBorderRgba,
        transientFontRgba,
        transientFillRgba,
    };
};
export const useGetActiveArComponentIds = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    if (!activeSceneId)
        return [];
    const scene = componentsById[activeSceneId];
    if (!scene)
        return [];
    if (scene?.trackingType !== ITrackingTypes.face)
        return scene?.children.filter((entityId) => !isAbstractComponent(componentsById[entityId])) || [];
    const faceLandmarkGroupIds = scene.children.filter((componentId) => componentsById[componentId].type == IComponentType.FaceLandmarkGroup);
    return faceLandmarkGroupIds.reduce((acc, faceLandmarkId) => {
        const faceLandmarkGroup = componentsById[faceLandmarkId];
        return [...acc, ...faceLandmarkGroup.children];
    }, []);
};
export const useGetSceneEntities = (enabled = true, isPreview = false) => {
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    const isReplaceEntityMode = useIsReplaceEntityMode();
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const activeScene = componentsById[activeSceneId];
    const sceneTrackingType = useGetActiveSceneTrackingType();
    const hiddenEntityIds = useSelector((state) => {
        return Object.keys(state.contentReducer.contentDoc.componentsById).filter((entityId) => !!state.contentReducer.contentDoc.componentsById[entityId].isHidden);
    });
    // Canvas content
    const canvasContent = useMemo(() => {
        return createCanvasR3fComponents({
            ids: activeScene?.children || [],
            hiddenEntityIds,
            componentsById,
            enabled,
            sceneTrackingType,
            isPreview,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeScene?.children?.length, hiddenEntityIds?.length, activeSceneId, isScreenRelativeMode, isReplaceEntityMode, sceneTrackingType, Object.keys(componentsById)]);
    return canvasContent;
};
export const useIsArModeDisabled = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    return useSelector((state) => state.contentReducer.contentDoc.componentsById[activeSceneId || '']?.trackingType === ITrackingTypes.noTrackingScreen);
};
export const useGetSceneIdList = () => {
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    return componentsById[rootId].children;
};
// Get the anchor group ID at position argument for the currently active scene
export const useGetAnchorGroupId = (position) => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const screenContentId = useGetActiveSceneScreenContentId();
    const anchorGroupIds = componentsById[screenContentId].children;
    for (let i = 0; i < anchorGroupIds.length; i++) {
        const anchorGroup = componentsById[anchorGroupIds[i]];
        if (anchorGroup.anchorPositionType == position) {
            return anchorGroupIds[i];
        }
    }
    return '';
};
// Does the active scene have a snapshot UI?
export const useGetSnapshotUiId = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const screenComponentIds = useGetActiveScreenComponentIds(false);
    for (let i = 0; i < screenComponentIds.length; i++) {
        const component = componentsById[screenComponentIds[i]];
        if (component.type == IComponentType.Button && (component.subCategory === IButtonSubCategory.snapshot || component.subCategory === IButtonSubCategory.recording))
            return component.id;
    }
    return null;
};
export const useGetSceneCameraButtonType = () => {
    const snapshotButtonId = useGetSnapshotUiId();
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!snapshotButtonId)
        return null;
    return componentsById[snapshotButtonId].subCategory || null;
};
// Returns an array of options for use in a <Dropdown> component
export const useGetSceneOptions = (disableActive = true) => {
    const initialOptions = [
        {
            text: 'Select a scene...',
            val: 'default',
        },
        {
            text: 'All scenes',
            val: 'allScenes',
            type: IMenuItemType.standard,
        },
    ];
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const sceneIds = useGetSceneIdList();
    return [
        ...initialOptions,
        ...sceneIds.map((sceneId) => {
            return {
                val: sceneId,
                text: componentsById[sceneId].title,
                type: disableActive && sceneId == activeSceneId ? IMenuItemType.disabled : IMenuItemType.standard,
            };
        }),
    ];
};
// Gets the screen relative scene ID for the currently active scene
export const useGetActiveSceneScreenContentId = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    return useGetSceneScreenContentId(activeSceneId);
};
// Gets the screen components for the currently active scene
export const useGetActiveScreenComponentIds = (filterOutFunctionalButtons = true) => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    return useGetScreenComponents(activeSceneId, filterOutFunctionalButtons);
};
export const useGetFaceTrackedComponentIds = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeScene = componentsById[activeSceneId];
    const entityIds = [];
    if (!activeScene)
        return [];
    for (let i = 0; i < activeScene.children.length; i++) {
        const id = activeScene.children[i];
        const component = componentsById[id];
        if (!component)
            continue;
        if (component.type === IComponentType.FaceLandmarkGroup) {
            entityIds.push(...component.children);
        }
    }
    return entityIds;
};
// Gets the screen relative scene ID, given a scene ID where sceneId is the ID of ISceneComp component
export const useGetSceneScreenContentId = (sceneId) => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeSceneChildren = componentsById[sceneId]?.children || [];
    return activeSceneChildren.filter((id) => {
        if (!componentsById[id])
            return false;
        return componentsById[id].type === IComponentType.ScreenContent;
    })[0];
};
export const useIs360ContentMissing = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const rootChildren = componentsById[rootId]?.children || [];
    return rootChildren.filter(id => {
        const scene = componentsById[id];
        return scene.trackingType === ITrackingTypes.content360 && !scene.content360?.filestoreId;
    }).length > 0;
};
// Gets the screen components for a given scene where sceneId is the ID of ISceneComp component
export const useGetScreenComponents = (sceneId, filterOutFunctionalButtons = false) => {
    const screenSceneId = useGetSceneScreenContentId(sceneId);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!screenSceneId)
        return []; // If no scene ID match
    const activeScreenContent = componentsById[screenSceneId];
    return activeScreenContent.children.reduce((idArray, anchorId) => {
        const anchorGroup = componentsById[anchorId];
        const anchorChildren = filterOutFunctionalButtons ? anchorGroup.children.filter(id => {
            const entity = componentsById[id];
            if (!entity)
                return false;
            if (isAbstractComponent(entity))
                return false;
            if (entity.type === IComponentType.Button && entity.category === IButtonCategory.functional) {
                return false;
            }
            return true;
        }) : anchorGroup.children;
        return [...idArray, ...anchorChildren];
    }, []);
};
export const useGetActiveScreenAnchorGroupIdDict = () => {
    const activeSceneScreenContentId = useGetActiveSceneScreenContentId();
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeScreenContent = componentsById[activeSceneScreenContentId];
    return activeScreenContent.children.reduce((dict, anchorId) => {
        const anchorGroup = componentsById[anchorId];
        dict[anchorGroup.anchorPositionType] = anchorId;
        return dict;
    }, {});
};
export const useGetAnchorPositionByEntityIds = (ids, activeScreenContentId) => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!ids)
        return undefined;
    const screenContent = componentsById[activeScreenContentId];
    const anchorPositions = [];
    for (let i = 0; i < ids.length; i++) {
        screenContent.children.forEach((id) => {
            const anchorGroup = componentsById[id];
            if (anchorGroup.children.includes(ids[i])) {
                anchorPositions.push(anchorGroup.anchorPositionType);
            }
        });
    }
    const allPositionsEqual = anchorPositions.every((val) => val === anchorPositions[0]);
    return allPositionsEqual ? anchorPositions[0] : null;
};
export const useSceneTitleDict = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    return Object.keys(componentsById).reduce((sceneTitleDict, entityId) => {
        if (componentsById[entityId].type === IComponentType.Scene)
            sceneTitleDict[entityId] = componentsById[entityId].title;
        return sceneTitleDict;
    }, {});
};
export const useEntityActionsHaveChanged = (entity) => {
    return useMemo(() => {
        return entity?.hasOwnProperty('actions') ?? false;
    }, [entity]);
};
export const useEntityTransitionsHaveChanged = (entity) => {
    return useMemo(() => {
        return (entity?.hasOwnProperty('transitions') &&
            (entity?.transitions?.hasOwnProperty('enter') || entity?.transitions?.hasOwnProperty('exit')));
    }, [entity]);
};
// function arraysHaveDifferences(a?: (number | string)[], b?: (number | string)[]) {
//     if (a?.length !== b?.length) return true;
//     for (let i = 0; i < (a?.length ?? 0); i++) {
//       if (a?.[i] !== b?.[i]) return true;
//     }
//     return false;
// }
// TODO - fix font issue in here
export const useEntityAppearanceHasChanged = (entity) => {
    return useMemo(() => {
        switch (entity?.type) {
            case IComponentType.Button:
                if (!entity.svgUrl) {
                    // 2 types of non-SVG button - white filled / blue filled - covered here
                    // Possible edge case user changes white preset to blue preset, won't flag as changed
                    // but without recording initial type in additional state, no way to know
                    // Similarly 3x preset border radius' are valid as 'non-changed'
                    if (entity.borderRadius !== 0.35 && entity.borderRadius !== 0 && entity.borderRadius !== 1)
                        return true;
                    if (entity.borderWidth !== 0 && entity.borderWidth !== 0.01)
                        return true;
                    if (entity.fontFamily !== IFontTypes.Roboto400)
                        return true;
                    if (entity.fontSize !== 0.11)
                        return true;
                    if (entity.textAlignment !== 'center')
                        return true;
                    if ((entity.color[0] !== 150 || entity.color[1] !== 191 || entity.color[2] !== 239 || entity.color[3] !== 1) &&
                        (entity.color[0] !== 255 || entity.color[1] !== 255 || entity.color[2] !== 255 || entity.color[3] !== 0))
                        return true;
                    if ((entity?.borderRgba?.[0] !== 52 || entity.borderRgba[1] !== 75 || entity.borderRgba[2] !== 96 || entity.borderRgba[3] !== 1) &&
                        (entity?.borderRgba?.[0] !== 150 || entity.borderRgba[1] !== 191 || entity.borderRgba[2] !== 239 || entity.borderRgba[3] !== 1))
                        return true;
                    if ((entity.fontRgba[0] !== 255 || entity.fontRgba[1] !== 255 || entity.fontRgba[2] !== 255 || entity.fontRgba[3] !== 1) &&
                        (entity.fontRgba[0] !== 150 || entity.fontRgba[1] !== 191 || entity.fontRgba[2] !== 239 || entity.fontRgba[3] !== 1))
                        return true;
                    return false;
                }
                else {
                    if (entity.borderRadius !== 0)
                        return true;
                    if (entity.borderWidth !== 0)
                        return true;
                    if (entity.fontFamily !== IFontTypes.Roboto400)
                        return true;
                    if (entity.fontSize !== 0.11)
                        return true;
                    if (entity.textAlignment !== 'center')
                        return true;
                    if (entity.color[0][0] !== 255 || entity.color[0][1] !== 255 || entity.color[0][2] !== 255 || entity.color[0][3] !== 0)
                        return true;
                    if (entity.borderRgba?.[0] !== 150 || entity.borderRgba?.[1] !== 191 || entity.borderRgba?.[2] !== 239 || entity.borderRgba[3] !== 1)
                        return true;
                    if (entity.fontRgba[0] !== 255 || entity.fontRgba[1] !== 255 || entity.fontRgba[2] !== 255 || entity.fontRgba[3] !== 0)
                        return true;
                    return false;
                }
            case IComponentType.Image:
            case IComponentType.Video:
                if (entity.hasOwnProperty('opacity') && entity.opacity !== 1)
                    return true;
                if (entity.hasOwnProperty('borderRadius') && entity.borderRadius !== 0)
                    return true;
                if (entity.hasOwnProperty('borderWidth') && entity.borderWidth !== 0)
                    return true;
                if (entity.hasOwnProperty('borderRgba') && (entity?.borderRgba?.[0] !== 150 || entity.borderRgba[1] !== 191 || entity.borderRgba[2] !== 239 || entity.borderRgba[3] !== 1))
                    return true;
                return false;
            case IComponentType.Text:
                if (entity.fontFamily !== IFontTypes.Roboto400 && entity.fontFamily !== IFontTypes.Roboto700)
                    return true;
                // These cover heading, subheading and paragraph default colors
                if ((entity.fontRgba[0] !== 52 || entity.fontRgba[1] !== 75 || entity.fontRgba[2] !== 96 || entity.fontRgba[3] !== 1) &&
                    (entity.fontRgba[0] !== 124 || entity.fontRgba[1] !== 137 || entity.fontRgba[2] !== 150 || entity.fontRgba[3] !== 1) &&
                    (entity.fontRgba[0] !== 102 || entity.fontRgba[1] !== 114 || entity.fontRgba[2] !== 125 || entity.fontRgba[3] !== 1))
                    return true;
                return false;
            case IComponentType.Model3d:
                return false; // Has no appearance shelf
            default:
                return false;
        }
    }, [entity]);
};
export const useProjectHasScenesWithImageTracking = (componentsById, rootId) => {
    return useMemo(() => {
        const sceneIds = componentsById[rootId]?.children;
        for (let i = 0; i < sceneIds.length; i++) {
            const sceneId = sceneIds[i];
            if (componentsById[sceneId]?.trackingType === ITrackingTypes.image) {
                return true;
            }
        }
        return false;
    }, [componentsById, rootId]);
};
export const useProjectHasScenesWithWorldTracking = () => {
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById);
    const rootId = useSelector((state) => state.contentReducer?.contentDoc?.rootComponentId);
    return useMemo(() => {
        const sceneIds = componentsById[rootId]?.children;
        for (let i = 0; i < sceneIds.length; i++) {
            const sceneId = sceneIds[i];
            if (componentsById[sceneId]?.trackingType === ITrackingTypes.world) {
                return true;
            }
        }
        return false;
    }, [componentsById, rootId]);
};
const getLocalState = (stateId) => {
    const localState = localStorage.getItem(stateId);
    if (localState === 'true')
        return true;
    if (localState === 'false')
        return false;
    return null;
};
export const usePreviewWithAppClipsState = () => {
    const [_, triggerRender] = useState(0);
    const projectHasWorldTracking = useProjectHasScenesWithWorldTracking();
    const projectId = useSelector((state) => state.userReducer.project.id);
    const stateId = `preview_with_app_clips_${projectId}`;
    const get = () => {
        const localAppClipState = getLocalState(stateId);
        return localAppClipState ?? projectHasWorldTracking;
    };
    const set = (appClipsEnabled) => {
        localStorage.setItem(stateId, appClipsEnabled ? 'true' : 'false');
        triggerRender(prev => prev + 1);
    };
    return { get, set };
};
export const useShowAppClipsModalLocalStorage = () => {
    const dispatch = useDispatch();
    const [_, triggerRender] = useState(0);
    const projectId = useSelector((state) => state.userReducer.project.id);
    const stateId = `show_app_clips_modal_${projectId}`;
    const get = () => {
        const localAppClipState = getLocalState(stateId);
        return localAppClipState;
    };
    const set = (show) => {
        localStorage.setItem(stateId, show ? 'true' : 'false');
        dispatch(onSetShowAppClipsModal(show));
        triggerRender(prev => prev + 1);
    };
    return { get, set };
};
const monthDiff = (d1, d2) => {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
};
const getMonthsSinceWhatsNewVersion = (whatsNewData) => {
    const currDate = new Date();
    const itemDate = new Date(whatsNewData.timestamp);
    return monthDiff(itemDate, currDate);
};
const WHATS_NEW_PREFIX = 'WHATS_NEW_VERSION';
export const getShowWhatsNewLocalStorageState = (version) => {
    const whatsNewString = localStorage.getItem(`${WHATS_NEW_PREFIX}_${version}`);
    if (!whatsNewString)
        return true;
    const whatsNewObject = JSON.parse(whatsNewString);
    return whatsNewObject.show;
};
export const useWhatsNewLocalStorage = (version) => {
    const dispatch = useDispatch();
    // Cleanup any items older than six months
    useLayoutEffect(() => {
        Object.entries(window.localStorage).forEach(([key, val]) => {
            if (key.includes(WHATS_NEW_PREFIX)) {
                const whatsNewData = JSON.parse(val);
                const months = getMonthsSinceWhatsNewVersion(whatsNewData);
                if (months > 6)
                    localStorage.removeItem(key);
            }
        });
    }, []);
    const get = () => getShowWhatsNewLocalStorageState(version);
    const set = (show) => {
        const whatsNewData = { show, timestamp: new Date().toISOString() };
        localStorage.setItem(`${WHATS_NEW_PREFIX}_${version}`, JSON.stringify(whatsNewData));
        dispatch(onSetWhatsNewTrigger());
    };
    return { get, set };
};
export const useGetActionTargetIdsByIdAndTrigger = (triggerType, id) => {
    const componentDict = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    //const selectedId = useSelector((state: IDesignerState) => state.userReducer.selectedEntityIds![0]);
    return getAllActionTargetIdsFromEntityForTrigger(componentDict[id], triggerType);
};
export const useAnimatedModelIdsOnActiveScene = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    const faceTrackedEntityIds = useGetFaceTrackedComponentIds();
    const sceneChildren = useMemo(() => componentsById[activeSceneId].children, [componentsById, activeSceneId]);
    return useMemo(() => {
        return [...sceneChildren, ...faceTrackedEntityIds].filter((entityId) => {
            const entity = componentsById[entityId];
            if (entity.type === IComponentType.Model3d) {
                return !!entity.animations?.length;
            }
            return false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneChildren, componentsById]);
};
export const useVideoIdsOnActiveScene = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentDict = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    const activeScene = componentDict[activeSceneId];
    const faceTrackedEntityIds = useGetFaceTrackedComponentIds();
    const sceneChildren = useMemo(() => componentDict[activeSceneId].children, [componentDict, activeSceneId]);
    const srSpatialComponents = getSpatialSrComponentsForScene(componentDict, activeScene);
    return useMemo(() => {
        return [...sceneChildren, ...faceTrackedEntityIds, ...srSpatialComponents].filter((entityId) => componentDict[entityId].type == IComponentType.Video);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneChildren, componentDict]);
};
export const useEmitterIdsOnActiveScene = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentDict = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    const faceTrackedEntityIds = useGetFaceTrackedComponentIds();
    const sceneChildren = useMemo(() => componentDict[activeSceneId].children, [componentDict, activeSceneId]);
    return useMemo(() => {
        return [...sceneChildren, ...faceTrackedEntityIds].filter((entityId) => componentDict[entityId].type == IComponentType.Emitter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneChildren, componentDict]);
};
export const useSpatialEntityIdsOnActiveScene = () => {
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    const faceTrackedEntityIds = useGetFaceTrackedComponentIds();
    const screenComponentIds = useGetActiveScreenComponentIds();
    const sceneChildren = useMemo(() => componentsById[activeSceneId].children, [componentsById, activeSceneId]);
    return useMemo(() => {
        return [...sceneChildren, ...faceTrackedEntityIds, ...screenComponentIds].filter((entityId) => !isAbstractComponent(componentsById[entityId]));
    }, [sceneChildren, faceTrackedEntityIds, componentsById, screenComponentIds]);
};
export const useOnClickOutside = (ref, cb) => {
    const onClickOutside = (event) => {
        const outsideMenuClick = ref.current && !ref.current.contains(event.target);
        if (outsideMenuClick) {
            cb(event);
        }
    };
    useEffect(() => {
        document.addEventListener('pointerdown', onClickOutside);
        return () => {
            document.removeEventListener('pointerdown', onClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
export const useActiveSceneVideoTitlesDict = () => {
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    const selectedId = useSelector((state) => state.userReducer.selectedEntityIds?.[0]);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const activeScene = componentsById[activeSceneId];
    const arComponents = useGetActiveArComponentIds();
    const srSpatialComponents = getSpatialSrComponentsForScene(componentsById, activeScene);
    return useMemo(() => {
        if (!componentsById)
            return {};
        return [...arComponents, ...srSpatialComponents].reduce((acc, entityId) => {
            if (componentsById[entityId]?.type === IComponentType.Video && selectedId !== entityId) {
                acc[entityId] = componentsById[entityId].title;
            }
            return acc;
        }, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentsById, activeSceneId, selectedId]);
};
export const useActiveSceneEntityTitles = () => {
    const faceTrackedEntityIds = useGetFaceTrackedComponentIds();
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const sceneEntityIds = useSelector((state) => state.contentReducer.contentDoc.componentsById[activeSceneId]?.children || []);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const activeScene = componentsById[activeSceneId];
    const srSpatialComponents = getSpatialSrComponentsForScene(componentsById, activeScene);
    return [...sceneEntityIds, ...srSpatialComponents, ...faceTrackedEntityIds].reduce((sceneScopedTitles, entityId) => {
        const component = componentsById[entityId];
        if (isAbstractComponent(component))
            return sceneScopedTitles;
        sceneScopedTitles[entityId] = component.title;
        return sceneScopedTitles;
    }, {});
};
export const useIsSnappedCurvedEntitySelected = () => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    return useSelector((state) => {
        const entity = selectedEntityIds && selectedEntityIds[0] ? state.contentReducer.contentDoc.componentsById[selectedEntityIds[0]] : null;
        if (entity && isCurveComponent(entity) && entity.isSnappedToTarget)
            return true;
        return false;
    });
};
export const useIsEverySelectedEntityLocked = () => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!selectedEntityIds)
        return false;
    return selectedEntityIds?.every((componentId) => {
        const component = componentsById[componentId];
        if (isAbstractComponent(component))
            return false; // should never happen
        return component.isLocked;
    });
};
export const useGetMultiSelectedFontHeight = () => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!selectedEntityIds)
        return null;
    const buttonsAndText = selectedEntityIds.filter((componentId) => {
        const component = componentsById[componentId];
        return component.type === IComponentType.Button || component.type === IComponentType.Text || component.type === IComponentType.Text3d;
    });
    if (buttonsAndText.length === 0)
        return null;
    const firstFontHeight = componentsById[buttonsAndText[0]].fontSize;
    const allFontsSameFontHeight = buttonsAndText.every((componentId) => {
        const component = componentsById[componentId];
        return component.fontSize === firstFontHeight;
    });
    return allFontsSameFontHeight ? firstFontHeight : null;
};
export const useGetMultiSelectedFontFamily = () => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!selectedEntityIds)
        return null;
    const buttonsAndText = selectedEntityIds.filter((componentId) => {
        const component = componentsById[componentId];
        return component.type === IComponentType.Button || component.type === IComponentType.Text || component.type === IComponentType.Text3d;
    });
    if (buttonsAndText.length === 0)
        return null;
    const firstFontFamily = componentsById[buttonsAndText[0]].fontFamily;
    const allFontsSameFontFamily = buttonsAndText.every((componentId) => {
        const component = componentsById[componentId];
        return component.fontFamily === firstFontFamily;
    });
    return allFontsSameFontFamily ? firstFontFamily : null;
};
export const useGetAllMultiSelectedFontFamily = () => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!selectedEntityIds)
        return null;
    const buttonsAndText = selectedEntityIds.filter((componentId) => {
        const component = componentsById[componentId];
        return component.type === IComponentType.Button || component.type === IComponentType.Text || component.type === IComponentType.Text3d;
    });
    if (buttonsAndText.length === 0)
        return null;
    const allSelectedFonts = buttonsAndText.map((componentId) => {
        const component = componentsById[componentId];
        return component.fontFamily;
    });
    return allSelectedFonts;
};
export const useGetAllMultiSelectedTextAlignment = () => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    if (!selectedEntityIds)
        return null;
    const buttonsAndText = selectedEntityIds.filter((componentId) => {
        const component = componentsById[componentId];
        return component.type === IComponentType.Button || component.type === IComponentType.Text || component.type === IComponentType.Text3d;
    });
    if (buttonsAndText.length === 0)
        return null;
    const firstAlignment = componentsById[buttonsAndText[0]].textAlignment;
    const allFontsSameAlignment = buttonsAndText.every((componentId) => {
        const component = componentsById[componentId];
        return component.textAlignment === firstAlignment;
    });
    return allFontsSameAlignment ? firstAlignment : null;
};
export const useGetCurrentSceneId = () => {
    const rootId = useSelector((state) => state.contentReducer?.contentDoc?.rootComponentId);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    const firstSceneId = componentsById[rootId].children[0];
    return activeSceneId ?? firstSceneId;
};
export const useGetNextSceneId = () => {
    const rootId = useSelector((state) => state.contentReducer?.contentDoc?.rootComponentId);
    const activeSceneId = useSelector((state) => state.userReducer.activeSceneId);
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    const sceneIds = componentsById[rootId].children;
    const currentSceneIndex = sceneIds.indexOf(activeSceneId ?? sceneIds[0]);
    if (currentSceneIndex === sceneIds.length - 1)
        return sceneIds[0];
    return sceneIds[currentSceneIndex + 1];
};
export const useGetCurrentScene = () => {
    const currentSceneId = useGetCurrentSceneId();
    const componentsById = useSelector((state) => state.contentReducer?.contentDoc?.componentsById || null);
    return componentsById?.[currentSceneId];
};
export const useGetCurrentSceneTrackingData = () => {
    const activeScene = useGetCurrentScene();
    const activeTrackingImageId = activeScene?.trackingImageId;
    const targetImageDict = useSelector((state) => state.contentReducer.contentDoc.tracking?.[ITrackingTypes.image] ?? {});
    return targetImageDict[activeTrackingImageId ?? ''];
};
export const useGetActiveSceneBackgroundData = () => {
    const currentScene = useGetCurrentScene();
    return currentScene?.content360;
};
const intervalCache = {};
export const useVideoRenditionStatusHelpers = (ids, _ms) => {
    const dispatch = useDispatch();
    const ms = _ms ?? 1000;
    for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!intervalCache[id])
            intervalCache[id] = {};
    }
    const startVideoRenditionStatusIntervalCheck = () => {
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const { status } = store.getState().contentReducer?.contentDoc?.videoStatusDict?.[id] ?? {};
            if (intervalCache[id].intervalId)
                clearInterval(intervalCache[id].intervalId);
            if (status === "error")
                return; // TODO: handle error correctly in ui
            if (status === "complete") {
                clearHlsCacheById(return360VideoIdBySceneId(id));
                dispatch(onSetVideoProgress({ id, progress: 100 }));
                return;
            }
            intervalCache[id].intervalId = setInterval(async () => {
                const { progressUrl, status } = store.getState().contentReducer?.contentDoc?.videoStatusDict?.[id] ?? {};
                if (!progressUrl || !status)
                    return;
                const hlsJobStatus = await fetch(progressUrl);
                const hlsJobStatusData = await hlsJobStatus.json();
                dispatch(onSetVideoRenditionProgressData_Cn_Doc({ id, progressUrl, status: hlsJobStatusData.status }));
                dispatch(onSetVideoProgress({ id, progress: hlsJobStatusData.progress }));
                if (hlsJobStatusData.status === "error")
                    clearInterval(intervalCache[id].intervalId);
                if (hlsJobStatusData.status === "complete" || hlsJobStatusData.progress === 100) {
                    clearHlsCacheById(return360VideoIdBySceneId(id));
                    dispatch(onSetVideoRenditionProgressData_Cn_Doc({ id, progressUrl, status: 'complete' }));
                    dispatch(onSetVideoProgress({ id, progress: hlsJobStatusData.progress }));
                    clearInterval(intervalCache[id].intervalId);
                }
            }, ms);
        }
    };
    const clearVideoRenditionStatusIntervalCheck = () => {
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            clearInterval(intervalCache[id].intervalId);
        }
    };
    return { startVideoRenditionStatusIntervalCheck, clearVideoRenditionStatusIntervalCheck };
};
export const useGetVideoProgressData = () => {
    const videoStatusDict = useSelector((state) => state.contentReducer.contentDoc.videoStatusDict);
    const videoProgressById = useSelector((state) => state.userReducer.videoProgressById);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const data = [];
    if (!videoProgressById)
        return data;
    for (const key in videoStatusDict) {
        const { status } = videoStatusDict[key];
        if (status === 'error')
            continue;
        const scene = componentsById[key];
        if (!scene?.content360)
            continue;
        const { title } = scene.content360;
        const progress = videoProgressById[key];
        data.push({ status, title, progress });
    }
    return data;
};
export const useGetLaunchScreenData = () => {
    const projectInfo = useSelector((state) => state.userReducer.project);
    let launchScreen = useSelector((state) => state.contentReducer.contentDoc.launchScreen);
    if (!!launchScreen) {
        // Fill any missing data with default launch screen data
        launchScreen = {
            ...DEFAULT_LAUNCH_SCREEN,
            ...launchScreen
        };
        launchScreen.fields = {
            ...DEFAULT_LAUNCH_SCREEN.fields,
            ...(launchScreen.fields ?? {})
        };
    }
    else {
        launchScreen = DEFAULT_LAUNCH_SCREEN;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        launchScreen.fields.title.value = projectInfo.metaTitle ?? '';
        // // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // launchScreen.fields.iconUrl.value = (projectInfo as any).metaIconUrl ?? '';
    }
    return launchScreen;
};
export const useGetPublishErrorHandler = () => {
    const dispatch = useDispatch();
    return (err, defaultToastError) => {
        if (err.code === 'ProjectSizeLimitExceeded') {
            const projectSize = parseFloat(err.size.split(" ")[0]);
            dispatch(onSetProjectSizeLimit(Math.round(err.sizeLimitBytes * 0.00000095367432)));
            dispatch(onSetProjectSize(projectSize));
            dispatch(onOpenModal(IModal.projectSizeLimit));
        }
        else {
            dispatch(onAddToast(defaultToastError));
        }
    };
};
export const useConvertTriggersToUseAppClipsHandler = () => {
    const dispatch = useDispatch();
    const projectId = useSelector((state) => state.userReducer.project.id);
    return async (triggerArray) => {
        try {
            const promises = [];
            const _triggerArray = triggerArray ?? await getAppClipCompatibleTriggers(projectId);
            for (const trigger of _triggerArray) {
                if (trigger.useAppClip)
                    continue;
                if (trigger.type !== "qr-code" && trigger.type !== "deep-link")
                    continue;
                // console.log('trigger to convert: ', trigger);
                promises.push(zwClient.changeTriggerUseAppClip(trigger.id, true));
            }
            await Promise.all(promises);
            dispatch(onAddToast(ToastsData.AppClipUpdateSuccess));
            dispatch(onSetAppClipsConverted(true));
        }
        catch (err) {
            Sentry.captureException(err);
            dispatch(onAddToast(ToastsData.AppClipUpdateError));
        }
    };
};
export const useRevertAppClipsTriggerHandler = () => {
    const dispatch = useDispatch();
    const projectId = useSelector((state) => state.userReducer.project.id);
    return async (triggerArray) => {
        try {
            const promises = [];
            const _triggerArray = triggerArray ?? await getAppClipCompatibleTriggers(projectId);
            for (const trigger of _triggerArray) {
                if (!trigger.useAppClip)
                    continue;
                promises.push(zwClient.changeTriggerUseAppClip(trigger.id, false));
            }
            await Promise.all(promises);
            if (promises.length > 0)
                dispatch(onAddToast(ToastsData.AppClipUpdateSuccess));
            dispatch(onSetAppClipsConverted(true));
        }
        catch (err) {
            Sentry.captureException(err);
            dispatch(onAddToast(ToastsData.AppClipUpdateError));
        }
    };
};
export const usePublishProject = () => {
    const projectId = useSelector((state) => state.userReducer.project.id);
    const publishErrorHandler = useGetPublishErrorHandler();
    const dispatch = useDispatch();
    return async (cancelRef) => {
        try {
            if (typeof projectId === 'undefined')
                throw 'Project ID is undefined';
            const zipFile = await bundleProject();
            if (cancelRef?.current)
                return;
            const uploadRes = await zwClient.zml.uploadFile(zipFile, IFileType.UARPackage, () => { }); // TODO: display upload progress
            const versions = await zwClient.getProjectVersionsAll(projectId);
            const version = `v${versions.length + 1}.0`; // TODO: the version name format should be "v{number of publishes}.{number of previews}"
            const resp = await zwClient.publish(projectId, { version, packagekey: uploadRes.key });
            await checkPublishStatus(resp.statusUrl);
            // console.log('!!publish success!!')
            dispatch(onAddToast(ToastsData.PublishSuccess));
        }
        catch (err) {
            Sentry.captureException(err);
            publishErrorHandler(err, ToastsData.PublishError);
        }
    };
};
export const useContentMissing = () => {
    const isTrackingImageMissing = useIsImageTrackingMissing();
    const is360ContentMissing = useIs360ContentMissing();
    return { isEssentialContentMissing: isTrackingImageMissing || is360ContentMissing, isTrackingImageMissing, is360ContentMissing };
};
export const useGetImageUrlFromFilestoreId = (filestoreId) => {
    const [imageUrl, setImageUrl] = useState(null);
    useEffect(() => {
        if (typeof filestoreId === 'undefined' || filestoreId === '')
            return;
        if (filestoreId === null) {
            setImageUrl(null);
            return;
        }
        try {
            const fsImage = filestore.load(filestoreId);
            fsImage.getUrl()
                .then(url => setImageUrl(url))
                .catch(() => console.error('Failed to get icon url: ', filestoreId));
        }
        catch (err) {
            console.error('Failed to get icon url: ', err);
        }
    }, [filestoreId]);
    if (typeof filestoreId === 'undefined' || filestoreId === '')
        return undefined;
    return imageUrl;
};
export const useGetImageTitleFromFilestoreId = (filestoreId) => {
    const [iconTitle, setIconTitle] = useState(null);
    useEffect(() => {
        if (typeof filestoreId === 'undefined')
            return;
        if (filestoreId === null) {
            setIconTitle(null);
            return;
        }
        try {
            const fsImage = filestore.load(filestoreId);
            setIconTitle(fsImage.name);
        }
        catch (err) {
            console.error('Failed to get icon url: ', err);
        }
    }, [filestoreId]);
    if (typeof filestoreId === 'undefined')
        return undefined;
    return iconTitle;
};
export const useProjectHasWorldTrackedScenes = () => {
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const rootId = useSelector((state) => state.contentReducer.contentDoc.rootComponentId);
    const root = componentsById?.[rootId || ''];
    const numWorldTrackedScenes = root?.children?.filter(id => (componentsById?.[id]).trackingType === ITrackingTypes.world)?.length;
    return (numWorldTrackedScenes ?? 0) > 0;
};
// const resultCache: {[id: string]: any} = {}
// export const useSuspenseWithPromises = <T,>(thenables: Promise<T>[], fileId: string): T[] => {
//     if (resultCache[fileId]) return resultCache[fileId];
//     throw new Promise(resolve => {
//         Promise.allSettled(thenables).then(results => {
//             const values: T[] = []
//             for (const result of results) {
//                 if (result.status === 'fulfilled') values.push(result.value ) 
//             }
//             resultCache[fileId] = values;
//             resolve(values)
//         })
//     })
// }
export const useGetScreenRelativeScaleNormalizationFactor = (canvasHeight) => {
    const { size } = useThree();
    const isScreenRelativeMode = useSelector((state) => state.userReducer.isScreenRelativeMode);
    return getScreenRelativeScaleNormalizationFactor({ canvasHeight: canvasHeight ?? size.height, isScreenRelativeMode });
};
export const useGetSelectedEntityBillboardState = () => {
    const selectedEntityIds = useSelector((state) => state.userReducer.selectedEntityIds);
    const componentsById = useSelector((state) => state.contentReducer.contentDoc.componentsById);
    const allCylindricalBillboarded = selectedEntityIds.every((id) => componentsById[id].billboard?.[0] === true && componentsById[id].billboard?.[1] === false);
    const allSphericallBillboarded = selectedEntityIds.every((id) => componentsById[id].billboard?.[0] === true && componentsById[id].billboard?.[1] === true);
    return { cylindrical: allCylindricalBillboarded, spherical: allSphericallBillboarded, all: allCylindricalBillboarded || allSphericallBillboarded };
};
